<template>
  <div v-if="isOpen" class="drawer-backdrop" @click="emitClose">
    <!-- Drawer Container fixed at the bottom of the viewport -->
    <button
      class="drawer-close-button"
      @click="emitClose"
      aria-label="Close drawer"
      :style="{
        transform: isHidden ? 'translateY(-100px)' : 'translateY(0)',
      }"
    >
      <svg class="icon">
        <use href="@/assets/iconset.svg#compactCross"></use>
      </svg>
    </button>
    <div class="drawer-container" @click.stop>
      <!-- Content passed from parent -->
      <div class="drawer-content">
        <slot />
      </div>
      <!-- Copyright Footer -->
      <CopyrightFooter class="drawer-footer" />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from "vue";
import CopyrightFooter from "@/components/GenericComponents/CopyrightFooter.vue";

export default {
  name: "BottomDrawer",
  components: {
    CopyrightFooter,
  },
  props: {
    /**
     * Determines whether the drawer is visible.
     */
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["closeDrawer"],
  setup(props) {
    const isHidden = ref(false);
    const lastScrollPos = ref(0);
    let drawerContainer = null;

    const handleDrawerScroll = () => {
      if (!drawerContainer) return;

      const currentScrollPos = drawerContainer.scrollTop;
      const threshold = 50;

      if (Math.abs(currentScrollPos - lastScrollPos.value) < threshold) {
        return;
      }

      isHidden.value =
        currentScrollPos > lastScrollPos.value && currentScrollPos > 100;
      lastScrollPos.value = currentScrollPos;
    };

    const setupScrollListener = () => {
      nextTick(() => {
        drawerContainer = document.querySelector(".drawer-container");
        if (drawerContainer) {
          drawerContainer.addEventListener("scroll", handleDrawerScroll);
        }
      });
    };

    const cleanupScrollListener = () => {
      if (drawerContainer) {
        drawerContainer.removeEventListener("scroll", handleDrawerScroll);
        drawerContainer = null;
      }
    };

    // Watch for drawer open/close to setup/cleanup listeners
    watch(
      () => props.isOpen,
      (newValue) => {
        if (newValue) {
          setupScrollListener();
        } else {
          cleanupScrollListener();
          isHidden.value = false; // Reset when drawer closes
        }
      }
    );

    onBeforeUnmount(() => {
      cleanupScrollListener();
    });

    return { isHidden };
  },
  methods: {
    emitClose() {
      this.$emit("closeDrawer");
    },
  },
};
</script>

<style scoped>
/* Fullscreen backdrop */
.drawer-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--bg-main-dark);
  /* background-color: rgba(0, 0, 0, 0.7); */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  backdrop-filter: blur(5px);
  width: 100%;
  height: 100%;
}

/* Drawer container fixed at the bottom of the window */
.drawer-container {
  position: fixed;
  left: 50%;
  bottom: 0;
  top: 0;
  /* Start off-screen at bottom and slide up */
  transform: translate(-50%, 100%);
  animation: slideUp 0.3s forwards;
  background: var(--bg-main-dark);
  width: 100%;
  max-height: 100vh;
  max-height: 100dvh;
  padding: var(--largeSpace);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overflow-x: hidden;
  z-index: 4;
}

/* Close button */
.drawer-close-button {
  position: fixed;
  top: var(--space);
  right: var(--largeSpace);
  background: none;
  border: none;
  cursor: pointer;

  padding: 0;
  height: var(--doubleSpace);
  transition: transform 0.3s ease;
  z-index: 7;
  background-color: var(--bg-main-dark);
  padding: 3px 3px 2px 3px;
  border-radius: 50%;
  width: 33px;
  height: 33px;
}

.drawer-close-button.icon {
  width: 31px;
  height: 31px;
  fill: var(--grey-lighter);
}

.drawer-close-button:hover {
  transform: scale(1.1) !important;
}

/* Content wrapper inside the drawer */
.drawer-content {
  /* margin-top: var(--clickableHeight); */
  /* padding: var(--space); */
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  margin-bottom: var(--clickableHeight);
  /* height: -webkit-fill-available; */
  /* overflow: auto; */
}

/* Keyframe for sliding up */
@keyframes slideUp {
  from {
    transform: translate(-50%, 100%);
  }
  to {
    transform: translate(-50%, 0);
  }
}

@media screen and (max-width: 590px) {
  .drawer-container {
    position: fixed;

    padding: var(--largeSpace);
  }
}

.drawer-footer {
  margin-top: auto;
  padding-bottom: var(--space);
}
</style>
